import { GlAlert, GlModal, GlFormCharacterCount, GlFormGroup, GlFormInput, GlFormTextarea } from '/builds/gitlab/gitlab-ee/node_modules/@gitlab/ui/src/index.js?vue3';
import { isEqual } from 'lodash-es';
import { __, n__, s__ } from '~/locale';
import { getCurrentUser } from '~/lib/utils/common_utils';
import { confirmAction } from '/builds/gitlab/gitlab-ee/app/assets/javascripts/lib/utils/confirm_via_gl_modal/confirm_via_gl_modal.js?vue3';
import { isAbsolute, isValidURL } from '~/lib/utils/url_utility';
import { DECISION_TEXT_LENGTH_MAX, DECISION_TITLE_LENGTH_MAX, DECISION_DESCRIPTION_LENGTH_MAX, DECISION_RATIONALE_LENGTH_MAX, DECISION_REMAINING_COUNT_THRESHOLD } from './constants';
import { decisionSourceUrl, decisionText } from './utils';
import DecisionLogDeciderSelect from '/builds/gitlab/gitlab-ee/ee/app/assets/javascripts/work_items/components/decision_log/decision_log_decider_select.vue?vue3';
const emptyFields = ()=>({
        title: '',
        resolvedBy: null,
        description: '',
        resolutionRationale: '',
        sourceLink: ''
    });
const DISMISS_TRIGGERS = [
    'cancel',
    'headerclose',
    'esc',
    'backdrop'
];
const HOSTNAME_WITH_TLD = /\.[a-z]{2,}$/i;
export default {
    name: 'DecisionLogFormModal',
    components: {
        GlAlert,
        GlModal,
        GlFormCharacterCount,
        GlFormGroup,
        GlFormInput,
        DecisionLogDeciderSelect,
        GlFormTextarea
    },
    props: {
        visible: {
            type: Boolean,
            required: false,
            default: false
        },
        decision: {
            type: Object,
            required: false,
            default: null
        },
        fullPath: {
            type: String,
            required: false,
            default: ''
        },
        isGroup: {
            type: Boolean,
            required: false,
            default: false
        },
        participants: {
            type: Array,
            required: false,
            default: ()=>[]
        },
        saving: {
            type: Boolean,
            required: false,
            default: false
        },
        errorMessage: {
            type: String,
            required: false,
            default: ''
        }
    },
    emits: [
        'hide',
        'save'
    ],
    data () {
        return {
            fields: emptyFields(),
            initialFields: emptyFields(),
            selectedDecider: null,
            submitted: false
        };
    },
    computed: {
        isEditing () {
            return Boolean(this.decision);
        },
        modalTitle () {
            return this.isEditing ? s__('WorkItemDecisionLog|Edit decision') : s__('WorkItemDecisionLog|New decision');
        },
        actionPrimary () {
            return {
                text: this.isEditing ? __('Save changes') : s__('WorkItemDecisionLog|Create decision'),
                attributes: {
                    variant: 'confirm',
                    loading: this.saving
                }
            };
        },
        currentUser () {
            return getCurrentUser({
                useGlobalId: true
            });
        },
        defaultResolvedBy () {
            return this.currentUser?.id ?? null;
        },
        editedDecider () {
            const { resolvedBy } = this.decision ?? {};
            return typeof resolvedBy === 'object' ? resolvedBy : null;
        },
        seededDecider () {
            return this.isEditing ? this.editedDecider : this.currentUser;
        },
        isSourceLinkReadOnly () {
            return Boolean(this.decision?.noteUrl);
        },
        sourceLinkDescription () {
            return this.isSourceLinkReadOnly ? s__('WorkItemDecisionLog|Captured automatically from the source and cannot be changed.') : s__('WorkItemDecisionLog|Slack, Google Docs, GitLab, or anywhere the discussion lives.');
        },
        isTitleValid () {
            return this.fields.title.trim().length > 0 && !this.isTitleOverLimit;
        },
        isResolvedByValid () {
            return Boolean(this.fields.resolvedBy);
        },
        isDescriptionValid () {
            return this.fields.description.trim().length > 0 && !this.isDescriptionOverLimit;
        },
        isRationaleValid () {
            return !this.isRationaleOverLimit && !this.isClearing('resolutionRationale');
        },
        isSourceLinkCleared () {
            return !this.isSourceLinkReadOnly && this.isClearing('sourceLink');
        },
        // Editing an unresolved decision saves the text to its title, which allows less than an option.
        decisionTextLimit () {
            return this.isEditing && !this.decision.resolvedAt ? DECISION_TITLE_LENGTH_MAX : DECISION_TEXT_LENGTH_MAX;
        },
        isTitleOverLimit () {
            return this.fields.title.length > this.decisionTextLimit;
        },
        isDescriptionOverLimit () {
            return this.fields.description.length > DECISION_DESCRIPTION_LENGTH_MAX;
        },
        isRationaleOverLimit () {
            return this.fields.resolutionRationale.length > DECISION_RATIONALE_LENGTH_MAX;
        },
        titleInvalidFeedback () {
            return this.isTitleOverLimit ? '' : s__('WorkItemDecisionLog|Enter a summary of the decision');
        },
        descriptionInvalidFeedback () {
            return this.isDescriptionOverLimit ? '' : s__('WorkItemDecisionLog|Describe the context for this decision');
        },
        rationaleInvalidFeedback () {
            return this.isRationaleOverLimit ? '' : this.$options.i18n.cannotClear;
        },
        sourceLinkInvalidFeedback () {
            return this.isSourceLinkCleared ? this.$options.i18n.cannotClear : s__('WorkItemDecisionLog|Enter a full URL, like https://example.com');
        },
        isSourceLinkValid () {
            const sourceLink = this.fields.sourceLink.trim();
            if (this.isSourceLinkCleared) return false;
            if (!sourceLink) return true;
            if (!isAbsolute(sourceLink) || !isValidURL(sourceLink)) return false;
            return HOSTNAME_WITH_TLD.test(new URL(sourceLink).hostname);
        },
        isValid () {
            return this.isTitleValid && this.isResolvedByValid && this.isDescriptionValid && this.isRationaleValid && this.isSourceLinkValid;
        },
        isDirty () {
            return !isEqual(this.fields, this.initialFields);
        },
        discardConfirmMessage () {
            return this.isEditing ? s__('WorkItemDecisionLog|Are you sure you want to cancel editing this decision?') : s__('WorkItemDecisionLog|Are you sure you want to cancel creating this decision?');
        }
    },
    watch: {
        visible: {
            immediate: true,
            handler (visible) {
                if (visible) {
                    this.resetForm();
                }
            }
        },
        decision () {
            this.resetForm();
        }
    },
    methods: {
        resetForm () {
            const { resolvedBy, description, resolutionRationale } = this.decision ?? emptyFields();
            this.submitted = false;
            this.fields = {
                // Every settled option is shown, so an edit can replace all of them at once.
                title: decisionText(this.decision),
                resolvedBy: resolvedBy?.id ?? resolvedBy ?? this.defaultResolvedBy,
                description: description ?? '',
                resolutionRationale: resolutionRationale ?? '',
                // The link field shows whichever link the card points at, so an edit starts from what the
                // reader saw rather than from an empty box.
                sourceLink: decisionSourceUrl(this.decision)
            };
            this.initialFields = {
                ...this.fields
            };
            this.selectedDecider = this.seededDecider;
        },
        // The update mutation can replace a saved value but cannot clear it, so a cleared field would
        // save as a no-op and keep its old value.
        isClearing (field) {
            return this.isEditing && Boolean(this.initialFields[field].trim()) && !this.fields[field].trim();
        },
        stateFor (isFieldValid) {
            return this.submitted && !isFieldValid ? false : null;
        },
        overLimitText (count) {
            return n__('%d character over limit.', '%d characters over limit.', count);
        },
        remainingCountText (count) {
            if (count > DECISION_REMAINING_COUNT_THRESHOLD) return '';
            return n__('%d character remaining.', '%d characters remaining.', count);
        },
        onPrimary (event) {
            event.preventDefault();
            this.submitted = true;
            if (!this.isValid) return;
            this.$emit('save', {
                ...this.fields,
                // A server-derived note URL is not editable, so it is never sent back as a source link.
                sourceLink: this.isSourceLinkReadOnly ? '' : this.fields.sourceLink,
                resolvedBy: this.selectedDecider
            });
        },
        async onHide (event) {
            if (!DISMISS_TRIGGERS.includes(event.trigger) || !this.isDirty) return;
            event.preventDefault();
            const confirmed = await confirmAction(this.discardConfirmMessage, {
                primaryBtnText: __('Discard changes'),
                primaryBtnVariant: 'danger',
                cancelBtnText: s__('WorkItemDecisionLog|Continue editing')
            });
            if (confirmed) {
                this.$emit('hide');
            }
        }
    },
    i18n: {
        cannotClear: s__('WorkItemDecisionLog|You can change this value, but you cannot remove it.')
    },
    actionCancel: {
        text: __('Cancel')
    },
    modalId: 'decision-log-form-modal',
    sourceLinkPlaceholder: 'https://example.com',
    DECISION_DESCRIPTION_LENGTH_MAX,
    DECISION_RATIONALE_LENGTH_MAX
};
